import { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import * as R from 'ramda';

import {
  GET_BUDGET_CATEGORIES,
  GET_BUDGET_CHARTS,
  GET_BUDGET_UNITS,
} from '@atom/graph/budget';
import { Progress } from '@atom/mui';
import { BudgetChart } from '@atom/types/budget';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailCategories from './categoryTable/BudgetDetailCategories';
import ExpendituresChart from './expendituresChart/ExpendituresChart';
import BudgetDetailChildUnits from './unitsTable/BudgetDetailChildUnits';
import BudgetDetailContext from './BudgetDetailContext';
import BudgetDetailOverview from './BudgetDetailOverview';

const styles = {
  container: {
    marginTop: '90px',
    marginBottom: '10rem',
  },
  floatingSpinner: {
    position: 'fixed',
    width: '100%',
    top: '65%',
    zIndex: 10,
  },
};

const BudgetDetailUnit = () => {
  const {
    budget,
    parentBudgetUnit,
    childBudgetUnits,
    setChildBudgetUnits,
    budgetCategories,
    setBudgetCategories,
    categoryIds,
    comparisonBudgets,
    budgetTypes,
    budgetItemTemplateNames,
    excludeZeroBudgetItems,
    showChart,
    showTracking,
    showExpenditures,
    selectedItems,
    setSelectedItems,
  } = useContext(BudgetDetailContext);

  // Budget Chart API Calls
  const [budgetsRaw, setBudgetsRaw] = useState<BudgetChart[]>(null);
  const [
    fetchCharts,
    { loading: loadingChartData, data: chartData, error: chartError },
  ] = useLazyQuery(GET_BUDGET_CHARTS, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (chartData) {
      setBudgetsRaw(R.pathOr([], ['budgetCharts', 'budgetCharts'], chartData));
    }
  }, [chartData]);

  useEffect(() => {
    if (chartError) {
      setBudgetsRaw(null);
    }
  }, [chartError]);

  useEffect(() => {
    if (showExpenditures && budget && parentBudgetUnit && showChart) {
      fetchCharts({
        variables: {
          input: {
            budgetIds: [budget?.id, ...comparisonBudgets.map(({ id }) => id)],
            budgetUnitId: parentBudgetUnit?.id,
            categoryIds,
            itemNames: budgetItemTemplateNames,
            budgetTypes,
          },
        },
      });
    }
  }, [parentBudgetUnit, comparisonBudgets, showChart]);

  const [
    getBudgetUnits,
    { loading: loadingBudgetUnits, data: budgetUnitsData },
  ] = useLazyQuery(GET_BUDGET_UNITS, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (budgetUnitsData) {
      setChildBudgetUnits(budgetUnitsData?.budgetUnits.budgetUnits);
    }
  }, [budgetUnitsData]);

  const [
    getBudgetCategories,
    { loading: loadingBudgetCategories, data: budgetCategoriesData },
  ] = useLazyQuery(GET_BUDGET_CATEGORIES, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (budgetCategoriesData) {
      setBudgetCategories(
        budgetCategoriesData?.budgetCategories?.budgetCategories,
      );

      // If user is hiding budget items without a cost, ensure they are not selected
      if (excludeZeroBudgetItems) {
        const categoriesData =
          budgetCategoriesData?.budgetCategories?.budgetCategories;
        const allItemIds = R.chain(
          category => R.pluck('id', category?.budgetItems),
          categoriesData,
        );
        const selectedIdsInResults = [...selectedItems].filter(id =>
          allItemIds.includes(id),
        );

        setSelectedItems(new Set(selectedIdsInResults));
      }
    }
  }, [budgetCategoriesData]);

  // Get display categories or units
  // - update with parentBudgetUnit and filter changes
  const callUpdateChildren = () => {
    if (!isNilOrEmpty(parentBudgetUnit)) {
      if (parentBudgetUnit.hasBudgetItems === false) {
        setBudgetCategories(null);
        setChildBudgetUnits(null);
        getBudgetUnits({
          variables: {
            input: {
              budgetId: budget.id,
              parentBudgetUnitId: parentBudgetUnit.id,
              categoryIds,
              budgetItemTemplateNames,
              budgetTypes,
            },
          },
        });
      } else {
        setChildBudgetUnits(null);
        getBudgetCategories({
          variables: {
            input: {
              budgetId: budget.id,
              budgetUnitId: parentBudgetUnit.id,
              categoryIds,
              budgetItemTemplateNames,
              excludeZeroBudgetItems,
              budgetTypes,
            },
          },
        });
      }
    }
  };
  useEffect(callUpdateChildren, [parentBudgetUnit, excludeZeroBudgetItems]);

  const budgetCategoriesView = !R.isNil(budgetCategories) ? (
    <BudgetDetailCategories />
  ) : (
    <></>
  );

  const childTableView = R.isNil(childBudgetUnits) ? (
    budgetCategoriesView
  ) : (
    <BudgetDetailChildUnits />
  );

  const showFloatingSpinner =
    isNilOrEmpty(parentBudgetUnit) || loadingBudgetCategories;

  return (
    <div style={styles.container}>
      <BudgetDetailOverview />
      {showChart && showTracking && (
        <ExpendituresChart budgetsRaw={budgetsRaw} loading={loadingChartData} />
      )}
      {showFloatingSpinner && <Progress style={styles.floatingSpinner} />}
      {loadingBudgetUnits && <Progress size={75} />}
      {childTableView}
    </div>
  );
};

export default BudgetDetailUnit;
